/**
 * Reports the browser's view of connection quality as Sentry tags and context.
 *
 * Tags stay low-cardinality (link class and type); the numeric estimates
 * (RTT, throughput) go into structured context so they do not fragment issue
 * statistics. Chromium exposes `navigator.connection`; other browsers do not,
 * in which case this is a no-op.
 */

type ConnectionInformation = {
	effectiveType?: string;
	type?: string;
	rtt?: number;
	downlink?: number;
	saveData?: boolean;
	addEventListener(type: string, listener: () => void): void;
	removeEventListener(type: string, listener: () => void): void;
};

export type { ConnectionInformation };

type ConnectionSentry = Pick<typeof import('@sentry/sveltekit'), 'setTag' | 'setContext'>;

export function trackConnectionQuality(navigator: Navigator, Sentry: ConnectionSentry): () => void {
	const connection = (navigator as Navigator & { connection?: ConnectionInformation }).connection;
	if (!connection) return () => undefined;

	const report = () => {
		if (connection.effectiveType !== undefined) {
			Sentry.setTag('client.connection.effective_type', connection.effectiveType);
		}
		if (connection.type !== undefined) {
			Sentry.setTag('client.connection.type', connection.type);
		}
		if (connection.saveData !== undefined) {
			Sentry.setTag('client.connection.save_data', connection.saveData);
		}
		Sentry.setContext('connection', {
			effective_type: connection.effectiveType,
			type: connection.type,
			rtt_ms: connection.rtt,
			downlink_mbps: connection.downlink,
			save_data: connection.saveData
		});
	};

	report();
	connection.addEventListener('change', report);
	return () => connection.removeEventListener('change', report);
}
